import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Play,
  Square,
  RotateCw,
  Terminal,
  Cpu,
  MemoryStick,
  HardDrive,
  Network,
  Maximize2,
  Key,
  Eye,
  EyeOff,
  Copy,
  Check,
  Bot,
  Wrench,
  Zap,
  Shield,
  GitBranch,
  Database,
  Globe,
  FileCode,
  Package,
  RefreshCw,
  ChevronRight,
} from "lucide-react";
import { clsx } from "clsx";
import type { VM, Backup } from "@/types";
import { LoadingSpinner } from "@/components/common/LoadingSpinner";
import { VMResizeModal } from "@/components/vm/VMResizeModal";
import { SnapshotPanel } from "@/components/vm/SnapshotPanel";
import { VLANManager } from "@/components/vm/VLANManager";

interface VMDetailProps {
  vm: VM | undefined;
  backups: Backup[];
  isLoading: boolean;
  omerCliEnabled?: boolean;
  isActionPending?: boolean;
  onStart: (vmId: number) => void;
  onStop: (vmId: number) => void;
  onRestart: (vmId: number) => void;
  onOpenConsole: (vmId: number) => void;
  onToggleOmer?: (vmId: number) => void;
  onResetPassword?: () => void;
  isResettingPassword?: boolean;
}

const OMER_TOOLS = [
  { icon: Terminal, name: "Bash", desc: "Execute shell commands" },
  { icon: FileCode, name: "Read / Edit", desc: "File operations" },
  { icon: Database, name: "SQL", desc: "Database queries" },
  { icon: Globe, name: "HTTP", desc: "API requests" },
  { icon: Package, name: "Package", desc: "Install packages" },
  { icon: GitBranch, name: "Git", desc: "Version control" },
];

const OMER_SKILLS = [
  { icon: Zap, name: "/deploy", desc: "Deploy application stack", color: "text-amber-400" },
  { icon: Shield, name: "/harden", desc: "Security hardening", color: "text-emerald-400" },
  { icon: RefreshCw, name: "/update", desc: "System updates", color: "text-blue-400" },
  { icon: Database, name: "/backup", desc: "Create backup", color: "text-purple-400" },
  { icon: Globe, name: "/dns", desc: "DNS management", color: "text-cyan-400" },
  { icon: Wrench, name: "/diagnose", desc: "Troubleshoot issues", color: "text-rose-400" },
];

function formatBytes(bytes: number | null): string {
  if (!bytes || bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

export function VMDetail({
  vm,
  backups,
  isLoading,
  omerCliEnabled,
  isActionPending,
  onStart,
  onStop,
  onRestart,
  onOpenConsole,
  onToggleOmer,
  onResetPassword,
  isResettingPassword,
}: VMDetailProps) {
  const [showResize, setShowResize] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);

  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopied(field);
    setTimeout(() => setCopied(null), 2000);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!vm) {
    return (
      <div className="py-20 text-center">
        <p className="text-lg text-gray-400">Virtual machine not found.</p>
        <Link to="/vms" className="mt-2 text-blue-400 hover:underline">
          Back to VM list
        </Link>
      </div>
    );
  }

  const ramGB = (vm.memory_mb / 1024).toFixed(1);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/vms"
            className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-800 hover:text-gray-200"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-100">{vm.name}</h1>
            <p className="text-sm text-gray-400">
              VMID: {vm.vmid} | Node: {vm.node} | Tier {vm.service_tier}
            </p>
          </div>
          <span
            className={clsx(
              "rounded-full px-3 py-1 text-xs font-medium",
              vm.status === "running"
                ? "bg-green-500/20 text-green-400"
                : vm.status === "stopped"
                  ? "bg-gray-500/20 text-gray-400"
                  : "bg-yellow-500/20 text-yellow-400"
            )}
          >
            {vm.status}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {vm.status === "stopped" && (
            <button
              onClick={() => onStart(vm.id)}
              disabled={isActionPending}
              className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Play className="h-4 w-4" />
              Start
            </button>
          )}
          {vm.status === "running" && (
            <>
              <button
                onClick={() => onStop(vm.id)}
                disabled={isActionPending}
                className="inline-flex items-center gap-2 rounded-lg bg-yellow-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Square className="h-4 w-4" />
                Stop
              </button>
              <button
                onClick={() => onRestart(vm.id)}
                disabled={isActionPending}
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RotateCw className="h-4 w-4" />
                Restart
              </button>
              <button
                onClick={() => onOpenConsole(vm.id)}
                className="inline-flex items-center gap-2 rounded-lg bg-gray-700 px-4 py-2 text-sm font-medium text-gray-200 transition-colors hover:bg-gray-600"
              >
                <Terminal className="h-4 w-4" />
                Console
              </button>
            </>
          )}
          <button
            onClick={() => setShowResize(true)}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-700 px-4 py-2 text-sm font-medium text-gray-300 transition-colors hover:bg-gray-800"
          >
            <Maximize2 className="h-4 w-4" />
            Resize
          </button>
        </div>
      </div>

      {/* Resize Modal */}
      {showResize && vm && (
        <VMResizeModal vm={vm} onClose={() => setShowResize(false)} />
      )}

      {/* Resource Allocation */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-xl border border-gray-800 bg-gray-900/50 p-4">
          <div className="flex items-center gap-2">
            <Cpu className="h-5 w-5 text-blue-400" />
            <span className="text-sm text-gray-400">CPU</span>
          </div>
          <p className="mt-2 text-2xl font-bold text-gray-100">
            {vm.cores} cores
          </p>
        </div>
        <div className="rounded-xl border border-gray-800 bg-gray-900/50 p-4">
          <div className="flex items-center gap-2">
            <MemoryStick className="h-5 w-5 text-purple-400" />
            <span className="text-sm text-gray-400">RAM</span>
          </div>
          <p className="mt-2 text-2xl font-bold text-gray-100">{ramGB} GB</p>
        </div>
        <div className="rounded-xl border border-gray-800 bg-gray-900/50 p-4">
          <div className="flex items-center gap-2">
            <HardDrive className="h-5 w-5 text-green-400" />
            <span className="text-sm text-gray-400">Disk</span>
          </div>
          <p className="mt-2 text-2xl font-bold text-gray-100">
            {vm.disk_gb} GB
          </p>
        </div>
        <div className="rounded-xl border border-gray-800 bg-gray-900/50 p-4">
          <div className="flex items-center gap-2">
            <Network className="h-5 w-5 text-blue-400" />
            <span className="text-sm text-gray-400">IP Address</span>
          </div>
          <p className="mt-2 text-lg font-mono font-bold text-gray-100">
            {vm.ip_address ?? "Not assigned"}
          </p>
        </div>
      </div>

      {/* Credentials */}
      <div className="rounded-xl border border-gray-800 bg-gray-900/50 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Key className="h-5 w-5 text-amber-400" />
          <h2 className="text-lg font-semibold text-gray-200">Credentials</h2>
        </div>
        {vm.ci_user ? (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs text-gray-500">Username</label>
              <div className="flex items-center gap-2">
                <code className="flex-1 rounded-lg bg-gray-800 px-3 py-2 font-mono text-sm text-gray-200">
                  {vm.ci_user}
                </code>
                <button
                  onClick={() => handleCopy(vm.ci_user!, "user")}
                  className="rounded-lg border border-gray-700 p-2 text-gray-400 hover:bg-gray-800 hover:text-gray-200"
                >
                  {copied === "user" ? <Check className="h-3.5 w-3.5 text-green-400" /> : <Copy className="h-3.5 w-3.5" />}
                </button>
              </div>
            </div>
            <div>
              <label className="mb-1 block text-xs text-gray-500">Password</label>
              <div className="flex items-center gap-2">
                <code className="flex-1 rounded-lg bg-gray-800 px-3 py-2 font-mono text-sm text-gray-200">
                  {showPassword ? (vm.ci_password ?? "—") : "••••••••••••••••"}
                </code>
                <button
                  onClick={() => setShowPassword(!showPassword)}
                  className="rounded-lg border border-gray-700 p-2 text-gray-400 hover:bg-gray-800 hover:text-gray-200"
                >
                  {showPassword ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                </button>
                <button
                  onClick={() => handleCopy(vm.ci_password ?? "", "pass")}
                  className="rounded-lg border border-gray-700 p-2 text-gray-400 hover:bg-gray-800 hover:text-gray-200"
                >
                  {copied === "pass" ? <Check className="h-3.5 w-3.5 text-green-400" /> : <Copy className="h-3.5 w-3.5" />}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-400">
            Credentials not available — this VM was created before credential tracking was enabled.
            Default cloud-init user is <code className="rounded bg-gray-800 px-1.5 py-0.5 text-amber-300">admin</code> (or <code className="rounded bg-gray-800 px-1.5 py-0.5 text-amber-300">root</code> for older images).
          </p>
        )}
        {onResetPassword && (
          <div className="mt-4 flex items-start gap-3 border-t border-gray-800 pt-4">
            <button
              onClick={onResetPassword}
              disabled={isResettingPassword}
              title="Genere un nouveau mot de passe, regenere le cloud-init disk, redemarre la VM. A utiliser si le mot de passe actuel ne fonctionne plus (divergence /etc/shadow vs. DB)."
              className="inline-flex items-center gap-1.5 rounded border border-amber-700/50 bg-amber-900/20 px-3 py-2 text-sm text-amber-200 hover:bg-amber-900/40 disabled:opacity-50"
            >
              <Key className="h-4 w-4" />
              {isResettingPassword ? "Regeneration en cours..." : "Reinitialiser le mot de passe"}
            </button>
            <p className="flex-1 text-xs text-gray-500">
              Genere un nouveau password 16 chars, regenere le cloud-init disk
              et redemarre la VM. Utilise quand le password actuel ne fonctionne
              plus (cas typique : VM creee avant traçage credentials, ou
              divergence DB ↔ /etc/shadow).
            </p>
          </div>
        )}
      </div>

      {/* OMER AI Assistant — Claude Code style */}
      {omerCliEnabled && onToggleOmer && (
        <OmerCliPanel vm={vm} onToggleOmer={onToggleOmer} />
      )}

      {/* Networks (VLANs) */}
      <VLANManager vmId={vm.id} />

      {/* Snapshots */}
      <SnapshotPanel vmId={vm.id} />

      {/* Backup History */}
      <div className="rounded-xl border border-gray-800 bg-gray-900/50 p-6">
        <h2 className="mb-4 text-lg font-semibold text-gray-200">
          Backup History
        </h2>
        {backups.length === 0 ? (
          <p className="text-sm text-gray-400">No backups found for this VM.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-800">
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                    Date
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                    Type
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                    Size
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {backups.map((backup) => (
                  <tr key={backup.id}>
                    <td className="px-3 py-2 text-gray-300">
                      {new Date(backup.created_at).toLocaleString()}
                    </td>
                    <td className="px-3 py-2 capitalize text-gray-300">
                      {backup.type}
                    </td>
                    <td className="px-3 py-2 text-gray-300">
                      {formatBytes(backup.size_bytes)}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={clsx(
                          "rounded-full px-2 py-0.5 text-xs font-medium",
                          backup.status === "completed"
                            ? "bg-green-500/20 text-green-400"
                            : backup.status === "failed"
                              ? "bg-red-500/20 text-red-400"
                              : "bg-yellow-500/20 text-yellow-400"
                        )}
                      >
                        {backup.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── OMER CLI Panel — styled like Claude Code ─────────────────────────── */

function OmerCliPanel({ vm, onToggleOmer }: { vm: VM; onToggleOmer: (id: number) => void }) {
  const [autoAccept, setAutoAccept] = useState(false);
  const [showTools, setShowTools] = useState(true);

  return (
    <div className="overflow-hidden rounded-xl border border-violet-500/20 bg-gray-950">
      {/* Terminal title bar */}
      <div className="flex items-center justify-between border-b border-gray-800 bg-gray-900/80 px-4 py-2">
        <div className="flex items-center gap-3">
          {/* macOS-style traffic lights */}
          <div className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-red-500/80" />
            <span className="h-2.5 w-2.5 rounded-full bg-yellow-500/80" />
            <span className={clsx("h-2.5 w-2.5 rounded-full", vm.omer_enabled ? "bg-green-500/80" : "bg-gray-600")} />
          </div>
          <div className="flex items-center gap-2">
            <Bot className="h-4 w-4 text-violet-400" />
            <span className="text-sm font-semibold text-gray-200">omer-cli</span>
            <span className="rounded-md bg-violet-500/15 px-1.5 py-0.5 text-[10px] font-medium text-violet-400">
              v1.2.0
            </span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Auto-accept toggle */}
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-gray-500">Auto-accept</span>
            <button
              onClick={() => setAutoAccept(!autoAccept)}
              className={clsx(
                "relative inline-flex h-5 w-9 items-center rounded-full transition-colors",
                autoAccept ? "bg-amber-500" : "bg-gray-700"
              )}
            >
              <span
                className={clsx(
                  "inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform",
                  autoAccept ? "translate-x-[18px]" : "translate-x-0.5"
                )}
              />
            </button>
          </div>
          {/* Enable/disable toggle */}
          <button
            onClick={() => onToggleOmer(vm.id)}
            className={clsx(
              "relative inline-flex h-5 w-9 items-center rounded-full transition-colors",
              vm.omer_enabled ? "bg-violet-600" : "bg-gray-700"
            )}
            title={vm.omer_enabled ? "Disable OMER" : "Enable OMER"}
          >
            <span
              className={clsx(
                "inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform",
                vm.omer_enabled ? "translate-x-[18px]" : "translate-x-0.5"
              )}
            />
          </button>
        </div>
      </div>

      {/* Terminal body */}
      <div className="p-4 font-mono text-xs leading-relaxed">
        {vm.omer_enabled ? (
          <>
            {/* Session info */}
            <div className="mb-3 text-gray-500">
              <span className="text-violet-400">omer</span>
              <span className="text-gray-600"> @ </span>
              <span className="text-cyan-400">{vm.name}</span>
              <span className="text-gray-600"> ({vm.ip_address ?? "no ip"})</span>
            </div>

            {/* Status line */}
            <div className="mb-4 flex items-center gap-2">
              <span className="inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.5)]" />
              <span className="text-emerald-400">Connected</span>
              <span className="text-gray-600">|</span>
              <span className="text-gray-500">Node: {vm.node}</span>
              <span className="text-gray-600">|</span>
              <span className={clsx(
                autoAccept ? "text-amber-400" : "text-gray-500"
              )}>
                {autoAccept ? "auto-accept: on" : "auto-accept: off"}
              </span>
            </div>

            {/* Tools section */}
            <button
              onClick={() => setShowTools(!showTools)}
              className="mb-2 flex items-center gap-1 text-gray-500 transition-colors hover:text-gray-300"
            >
              <ChevronRight className={clsx("h-3 w-3 transition-transform", showTools && "rotate-90")} />
              <Wrench className="h-3 w-3" />
              <span>Tools ({OMER_TOOLS.length})</span>
            </button>

            {showTools && (
              <div className="mb-4 ml-4 grid grid-cols-2 gap-x-6 gap-y-1 sm:grid-cols-3">
                {OMER_TOOLS.map(tool => (
                  <div key={tool.name} className="flex items-center gap-2 py-0.5">
                    <tool.icon className="h-3 w-3 text-gray-600" />
                    <span className="text-blue-400">{tool.name}</span>
                    <span className="text-gray-700">—</span>
                    <span className="text-gray-500">{tool.desc}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Skills section */}
            <div className="mb-4">
              <div className="mb-2 flex items-center gap-1 text-gray-500">
                <Zap className="h-3 w-3" />
                <span>Skills</span>
              </div>
              <div className="ml-4 grid grid-cols-2 gap-x-6 gap-y-1 sm:grid-cols-3">
                {OMER_SKILLS.map(skill => (
                  <div key={skill.name} className="flex items-center gap-2 py-0.5">
                    <skill.icon className={clsx("h-3 w-3", skill.color)} />
                    <span className="text-violet-400">{skill.name}</span>
                    <span className="text-gray-600 hidden sm:inline">— {skill.desc}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Prompt */}
            <div className="flex items-center gap-2 border-t border-gray-800/50 pt-3">
              <span className="text-violet-400">$</span>
              <span className="text-gray-400">omer</span>
              <span className="text-gray-600">&gt;</span>
              <span className="inline-block h-4 w-1.5 animate-pulse bg-violet-400/70 rounded-sm" />
            </div>
          </>
        ) : (
          /* Disabled state */
          <div className="flex flex-col items-center py-6 text-center">
            <div className="mb-3 rounded-full bg-gray-800 p-3">
              <Bot className="h-6 w-6 text-gray-600" />
            </div>
            <p className="text-sm text-gray-400">OMER is disabled on this VM</p>
            <p className="mt-1 text-[11px] text-gray-600">
              Enable OMER to unlock AI-powered deployment, security hardening, and system management.
            </p>
            <button
              onClick={() => onToggleOmer(vm.id)}
              className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-violet-600/20 px-3 py-1.5 text-xs font-medium text-violet-300 transition-colors hover:bg-violet-600/30"
            >
              <Zap className="h-3 w-3" />
              Enable OMER
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
